import React, {Component, ReactNode} from "react"
import PropTypes from "prop-types"
import {Col} from "react-bootstrap"
import DatePicker, {registerLocale} from "react-datepicker"
import moment, {Moment} from "moment"
import omit from "lodash/omit"

import fr from "date-fns/locale/fr"
import enGB from "date-fns/locale/en-GB"
import getCurrentLocale from "@irati/utils/lang/helpers/getCurrentLocale"

import {ValidationInput} from "@irati/utils/validation"
import {InjectIntl, injectIntl} from "@irati/utils/lang"

import {FormGroup} from "../Form"
import ErrorText from "../ErrorText"
import IconText from "../IconText"

import "react-datepicker/dist/react-datepicker.css"

import {IconTypes} from "../Icon"
import {CalendarIcon, DatePickerWrapper} from "./styled/DatePicker"

registerLocale("fr-FR", fr)
registerLocale("en-GB", enGB)
registerLocale("en-AU", enGB)

interface Props extends InjectIntl {
  className?: string,
  hint?: string,
  name?: string,
  showTimeSelect?: boolean,
  icon?: boolean,
  value?: string,
  placeholderText?: string,
  dropdownMode?: string,
  showYearDropdown?: boolean,
  minDate?: Date,
  label: ReactNode | string,
  onChange: (date: Moment) => void
}

interface State {
  errorMsg: string | null,
  value: Date
}

export class StockAddDetailsDatePicker extends Component<Props, State> {
  validation: ValidationInput

  static defaultProps: Omit<Props, "intl" | "formatMessage" | "label" | "onChange"> = {
    className: "",
    placeholderText: "Select a date",
    dropdownMode: "select",
    showYearDropdown: true,
    minDate: moment()
      .add(1, "days")
      .toDate(),
    showTimeSelect: false,
    icon: false
  }

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      errorMsg: null,
      value: moment(props.value || undefined).toDate()
    }

    this.validation = new ValidationInput(this)
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const {value: rawValue} = props
    if (rawValue) {
      const value = moment(rawValue).toDate()
      const {value: stateValue} = state
      if (!stateValue || value.getTime() !== stateValue.getTime()) {
        return {value}
      }
      return null
    }
    return null
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    this.validation.update(prevProps, prevState)
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  handleChange = (value: Date) => {
    this.setState({value})
    this.props.onChange(moment(value))
  }

  preventRawEditing = (e: Event) => {
    e.preventDefault()
  }

  render() {
    const {label, hint, name, className, showTimeSelect, icon, formatMessage} = this.props
    const {errorMsg, value} = this.state

    const inputProps = omit(this.props, ["className", "name", "label", "hint", "value", "onChange", "validation"])

    return (
      <FormGroup className={className} controlId={name} validationState={errorMsg ? "error" : undefined}>
        <Col xs={12}>
          <label htmlFor={name}>{label}</label>
        </Col>
        <Col xs={12}>
          <DatePickerWrapper>
            <DatePicker
              locale={getCurrentLocale()}
              onChangeRaw={this.preventRawEditing}
              timeCaption={formatMessage({id: "platform.web.ui.datePicker.timeCaption"})}
              {...inputProps}
              timeFormat="H:mm"
              dateFormat={showTimeSelect ? "dd/MM/yyyy H:mm" : "dd/MM/yyyy"}
              selected={value}
              onChange={this.handleChange} />
            {icon && <CalendarIcon icon="calendar" />}
          </DatePickerWrapper>
          {errorMsg && (
            <div>
              <ErrorText small>{errorMsg}</ErrorText>
            </div>
          )}
          {hint && (
            <div>
              <IconText icon={IconTypes.info} small>{hint}</IconText>
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(StockAddDetailsDatePicker)
